import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const FileConverterConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['conversion', 'settings']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const formats = [
    { value: 'pdf', label: 'PDF', icon: '📕', category: 'document' },
    { value: 'docx', label: 'Word (DOCX)', icon: '📘', category: 'document' },
    { value: 'doc', label: 'Word (DOC)', icon: '📘', category: 'document' },
    { value: 'xlsx', label: 'Excel (XLSX)', icon: '📗', category: 'spreadsheet' },
    { value: 'xls', label: 'Excel (XLS)', icon: '📗', category: 'spreadsheet' },
    { value: 'pptx', label: 'PowerPoint (PPTX)', icon: '📙', category: 'presentation' },
    { value: 'txt', label: 'Szöveg (TXT)', icon: '📄', category: 'text' },
    { value: 'html', label: 'HTML', icon: '🌐', category: 'web' },
    { value: 'md', label: 'Markdown', icon: '📝', category: 'text' },
    { value: 'jpg', label: 'JPG', icon: '🖼️', category: 'image' },
    { value: 'png', label: 'PNG', icon: '🖼️', category: 'image' },
    { value: 'gif', label: 'GIF', icon: '🖼️', category: 'image' },
  ];

  const getFormatsByCategory = (category: string) => {
    return formats.filter(f => f.category === category);
  };

  const categories = [
    { value: 'document', label: 'Dokumentumok', icon: '📄' },
    { value: 'spreadsheet', label: 'Táblázatok', icon: '📊' },
    { value: 'presentation', label: 'Prezentációk', icon: '📊' },
    { value: 'image', label: 'Képek', icon: '🖼️' },
    { value: 'text', label: 'Szöveg', icon: '📝' },
    { value: 'web', label: 'Web', icon: '🌐' },
  ];

  return (
    <div className="space-y-3">
      {/* Konverzió */}
      <CollapsibleSection
        id="conversion"
        title="Konverzió Beállítások"
        icon="🔄"
        color="pink"
        isExpanded={expandedSections.has('conversion')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Forrás fájl
            </label>
            <input
              type="text"
              value={node.data.config.file || ''}
              onChange={(e) => updateConfig('file', e.target.value)}
              className={commonStyles.input}
              placeholder="{{file_path}} vagy /path/to/file.docx"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={commonStyles.label}>
                Forrás formátum
              </label>
              <select
                value={node.data.config.from || 'docx'}
                onChange={(e) => updateConfig('from', e.target.value)}
                className={commonStyles.select}
              >
                {categories.map((cat) => (
                  <optgroup key={cat.value} label={`${cat.icon} ${cat.label}`}>
                    {getFormatsByCategory(cat.value).map((format) => (
                      <option key={format.value} value={format.value}>
                        {format.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div>
              <label className={commonStyles.label}>
                Cél formátum
              </label>
              <select
                value={node.data.config.to || 'pdf'}
                onChange={(e) => updateConfig('to', e.target.value)}
                className={commonStyles.select}
              >
                {categories.map((cat) => (
                  <optgroup key={cat.value} label={`${cat.icon} ${cat.label}`}>
                    {getFormatsByCategory(cat.value).map((format) => (
                      <option key={format.value} value={format.value}>
                        {format.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>

          {/* Konverzió előnézet */}
          <div className="bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-md p-3">
            <div className="flex items-center justify-center space-x-3 text-sm">
              <span className="text-2xl">
                {formats.find(f => f.value === (node.data.config.from || 'docx'))?.icon}
              </span>
              <span className="font-semibold text-pink-700 dark:text-pink-300">
                {(node.data.config.from || 'docx').toUpperCase()}
              </span>
              <span className="text-pink-500">→</span>
              <span className="text-2xl">
                {formats.find(f => f.value === (node.data.config.to || 'pdf'))?.icon}
              </span>
              <span className="font-semibold text-pink-700 dark:text-pink-300">
                {(node.data.config.to || 'pdf').toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Beállítások */}
      <CollapsibleSection
        id="settings"
        title="Speciális Beállítások"
        icon="⚙️"
        color="blue"
        isExpanded={expandedSections.has('settings')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Kimeneti fájlnév (opcionális)
            </label>
            <input
              type="text"
              value={node.data.config.outputFilename || ''}
              onChange={(e) => updateConfig('outputFilename', e.target.value)}
              className={commonStyles.input}
              placeholder="converted_file vagy {{filename}}"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Ha üres, automatikus név generálódik
            </p>
          </div>

          {/* PDF specifikus beállítások */}
          {node.data.config.to === 'pdf' && (
            <>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="preserveFormatting"
                  checked={node.data.config.preserveFormatting !== false}
                  onChange={(e) => updateConfig('preserveFormatting', e.target.checked)}
                  className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                />
                <label htmlFor="preserveFormatting" className="text-sm text-gray-700 dark:text-gray-300">
                  Formázás megtartása
                </label>
              </div>

              <div>
                <label className={commonStyles.label}>
                  PDF minőség
                </label>
                <select
                  value={node.data.config.pdfQuality || 'high'}
                  onChange={(e) => updateConfig('pdfQuality', e.target.value)}
                  className={commonStyles.select}
                >
                  <option value="low">Alacsony (gyors)</option>
                  <option value="medium">Közepes</option>
                  <option value="high">Magas (lassú)</option>
                </select>
              </div>
            </>
          )}

          {/* Kép specifikus beállítások */}
          {['jpg', 'png', 'gif'].includes(node.data.config.to || '') && (
            <div>
              <label className={commonStyles.label}>
                Kép minőség (%)
              </label>
              <input
                type="range"
                value={node.data.config.imageQuality || 90}
                onChange={(e) => updateConfig('imageQuality', parseInt(e.target.value))}
                className="w-full"
                min="1"
                max="100"
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {node.data.config.imageQuality || 90}%
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A konverzió után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{converted_file}}', desc: 'Konvertált fájl URL', example: 'https://storage.../file.pdf' },
              { var: '{{file_size}}', desc: 'Fájl méret (byte)', example: '102400' },
              { var: '{{format}}', desc: 'Cél formátum', example: 'pdf' },
              { var: '{{success}}', desc: 'Sikeres konverzió', example: 'true' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="green"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📄 Dokumentum → PDF:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Word feltöltés → PDF konverzió → FlintSign aláírás
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🖼️ Kép konverzió:</strong>
            <div className="mt-1 font-mono text-[10px]">
              PNG → JPG konverzió → Tömörítés → Tárolás
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📊 Excel → PDF report:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Excel → PDF konverzió → Email melléklet
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default FileConverterConfig;
